import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Divider,
    Button,
    Grid,
    Chip,
    IconButton
} from '@mui/material';
import { styled } from '@mui/system';
import {
    Check,
    Edit2,
    Calendar,
    DollarSign,
    Clock,
    Phone,
    Info,
    ShieldCheck,
    AlertCircle,
    Smartphone,
    Wifi
} from 'lucide-react';

// Styled components
const SectionPaper = styled(Box)(({ theme }) => ({
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid #eaecf0',
    backgroundColor: '#fff',
    marginBottom: '24px',
    position: 'relative',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 600,
    color: '#111827',
}));

const DetailRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #f3f4f6',
    '&:last-child': {
        borderBottom: 'none',
    }
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
    color: '#4b5563',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
}));

const DetailValue = styled(Typography)(({ theme }) => ({
    color: '#111827',
    fontSize: '0.875rem',
    fontWeight: 500,
    textAlign: 'right',
}));

const TotalRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0 0',
    marginTop: '8px',
    borderTop: '1px solid #e5e7eb',
}));

const TotalLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '0.9375rem',
}));

const TotalValue = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '1.125rem',
    color: '#ef4444',
}));

const EditButton = styled(IconButton)(({ theme }) => ({
    padding: '6px',
    borderRadius: '8px',
    color: '#6b7280',
    '&:hover': {
        backgroundColor: '#f3f4f6',
    }
}));

const SubscriptionInfoBox = styled(Box)(({ theme }) => ({
    padding: '24px',
    backgroundColor: '#f0f9ff',
    borderRadius: '12px',
    borderLeft: '4px solid #3b82f6',
    marginBottom: '28px',
}));

const GuaranteeBox = styled(Box)(({ theme }) => ({
    backgroundColor: '#d1fae5',
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginTop: '24px',
    marginBottom: '24px',
}));

const DeviceStatusChip = styled(Chip)(({ theme }) => ({
    borderRadius: '16px',
    height: '24px',
    fontSize: '0.75rem',
    fontWeight: 500,
}));

const TransferReviewAndConfirm = ({ planDetails, userDetails, subscriptionDetails, onEdit }) => {
    if (!planDetails || !userDetails) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <AlertCircle size={40} color="#f43f5e" style={{ marginBottom: '16px' }} />
                <Typography variant="h6" gutterBottom>
                    Missing Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    We couldn't load all the necessary details. Please go back and complete the previous steps.
                </Typography>
                <Button 
                    variant="outlined" 
                    onClick={() => onEdit(0)} 
                    sx={{ mt: 2 }}
                >
                    Start Over
                </Button>
            </Box>
        );
    }
    
    // Extract subscription details
    const originalSubscription = planDetails.originalSubscription || {};
    const subscriptionType = originalSubscription.subscription_type || 'annual';
    const monthlySubscriptionCost = subscriptionType === 'annual' ? 8 : 12;
    const nextRenewalDate = originalSubscription.next_renewal_date || 'Not available'; // Default fallback

    // Formatted total monthly cost - CORRECTED: Don't add subscription cost again as it's already included in the billing
    const totalMonthly = (planDetails.monthlyRate || 0) + (planDetails.taxes || 0);
    
    // One-time costs
    const activationFee = planDetails.totalActivationFee || 35;
    const shippingCost = userDetails.hasEsim ? 0 : 
                         (subscriptionDetails.shipping === 'expedited' ? 15 : 8);
    const totalDueToday = activationFee + shippingCost;

    return (
        <Box>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 1, fontWeight: 600 }}>
                Review Your Transfer
            </Typography>
            <Typography variant="body2" gutterBottom align="center" sx={{ mb: 4, color: '#6b7280' }}>
                Please review your carrier transfer details before proceeding to payment.
            </Typography>
            
            {/* Current Subscription Info */}
            <SubscriptionInfoBox>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Info size={18} color="#3b82f6" style={{ marginRight: '8px' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Your Current Subscription
                    </Typography>
                </Box>
                
                <Grid container spacing={3} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={4}>
                        <DetailLabel sx={{ color: '#3b82f6' }}>
                            <Clock size={16} />
                            Subscription Type
                        </DetailLabel>
                        <Typography variant="body2" fontWeight={500}>
                            {subscriptionType === 'annual' ? 'Annual Plan' : '3 Months Plan'}
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <DetailLabel sx={{ color: '#3b82f6' }}>
                            <DollarSign size={16} />
                            Monthly Cost
                        </DetailLabel>
                        <Typography variant="body2" fontWeight={500}>
                            ${monthlySubscriptionCost}.00/mo
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <DetailLabel sx={{ color: '#3b82f6' }}>
                            <Calendar size={16} />
                            Next Renewal
                        </DetailLabel>
                        <Typography variant="body2" fontWeight={500}>
                            {nextRenewalDate}
                        </Typography>
                    </Grid>
                </Grid>
                
                <Typography variant="body2" sx={{ mt: 1, color: '#4b5563' }}>
                    Your CircledIn subscription will continue with your current plan. No additional subscription charges will be applied.
                </Typography>
            </SubscriptionInfoBox>
            
            {/* Plan Details Section */}
            <SectionPaper>
                <SectionHeader>
                    <SectionTitle>Plan Details</SectionTitle>
                    <EditButton
                        onClick={() => onEdit(1)}
                        aria-label="Edit plan details"
                    >
                        <Edit2 size={16} />
                    </EditButton>
                </SectionHeader>
                
                <DetailRow>
                    <DetailLabel>
                        <Phone size={16} />
                        Circle Name:
                    </DetailLabel>
                    <DetailValue>{planDetails.circleName || 'N/A'}</DetailValue>
                </DetailRow>
                
                <DetailRow>
                    <DetailLabel>New Carrier Plan:</DetailLabel>
                    <DetailValue>
                        {planDetails.planName || 'N/A'} 
                        {planDetails.carrier && ` (${planDetails.carrier.charAt(0).toUpperCase() + planDetails.carrier.slice(1)})`}
                    </DetailValue>
                </DetailRow>
                
                <DetailRow>
                    <DetailLabel>Number of Lines:</DetailLabel>
                    <DetailValue>{planDetails.lines || 1}</DetailValue>
                </DetailRow>
            </SectionPaper>
            
            {/* Plan Pricing Section */}
            <SectionPaper>
                <SectionTitle sx={{ mb: 2 }}>Plan Pricing</SectionTitle>
                
                <DetailRow>
                    <DetailLabel>Monthly Rate:</DetailLabel>
                    <DetailValue>${planDetails.monthlyRate?.toFixed(2) || '0.00'}/mo</DetailValue>
                </DetailRow>
                
                <DetailRow>
                    <DetailLabel>Taxes & Fees:</DetailLabel>
                    <DetailValue>${planDetails.taxes?.toFixed(2) || '0.00'}/mo</DetailValue>
                </DetailRow>
                
                <TotalRow>
                    <TotalLabel>Total Monthly Cost:</TotalLabel>
                    <TotalValue>
                        ${totalMonthly.toFixed(2)}/mo
                    </TotalValue>
                </TotalRow>
            </SectionPaper>
            
            {/* Device Details Section */}
            <SectionPaper>
                <SectionHeader>
                    <SectionTitle>Device Details</SectionTitle>
                    <EditButton
                        onClick={() => onEdit(2)}
                        aria-label="Edit device details"
                    >
                        <Edit2 size={16} />
                    </EditButton>
                </SectionHeader>
                
                {userDetails.lineDetails && userDetails.lineDetails.map((line, index) => (
                    <Box key={index}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Line {line.lineNumber}
                            </Typography>
                            <DeviceStatusChip 
                                icon={<Wifi size={14} />}
                                label={line.esim ? "eSIM" : "Physical SIM"}
                                size="small"
                                sx={{ 
                                    backgroundColor: line.esim ? '#eef2ff' : '#eff6ff',
                                    color: line.esim ? '#4f46e5' : '#2563eb',
                                    borderColor: 'transparent'
                                }}
                            />
                        </Box>
                        
                        <DetailRow>
                            <DetailLabel>
                                <Smartphone size={16} />
                                IMEI:
                            </DetailLabel>
                            <DetailValue>{line.imei || 'Not provided'}</DetailValue>
                        </DetailRow>
                        
                        {line.esim && line.imei2 && (
                            <DetailRow>
                                <DetailLabel>Secondary IMEI:</DetailLabel>
                                <DetailValue>{line.imei2}</DetailValue>
                            </DetailRow>
                        )}
                        
                        {index < userDetails.lineDetails.length - 1 && <Divider sx={{ my: 2 }} />}
                    </Box>
                ))}
            </SectionPaper>
            
            {/* Delivery Details Section */}
            <SectionPaper>
                <SectionHeader>
                    <SectionTitle>Delivery Details</SectionTitle>
                    <EditButton
                        onClick={() => onEdit(3)}
                        aria-label="Edit delivery details"
                    >
                        <Edit2 size={16} />
                    </EditButton>
                </SectionHeader>
                
                <DetailRow>
                    <DetailLabel>Delivery Method:</DetailLabel>
                    <DetailValue>
                        {userDetails.hasEsim ? 
                            'Digital eSIM Delivery (Free)' : 
                            subscriptionDetails.shipping === 'expedited' ? 
                                'Expedited Shipping ($15.00)' : 
                                'Standard Shipping ($8.00)'
                        }
                    </DetailValue>
                </DetailRow>
                
                {!userDetails.hasEsim && (
                    <DetailRow>
                        <DetailLabel>Estimated Delivery:</DetailLabel>
                        <DetailValue>
                            {subscriptionDetails.shipping === 'expedited' ? 
                                '2-3 business days' : 
                                '5-7 business days'
                            }
                        </DetailValue>
                    </DetailRow>
                )}
                
                {userDetails.zip_code && (
                    <DetailRow>
                        <DetailLabel>Shipping Address:</DetailLabel>
                        <DetailValue>Will use your account address (ZIP: {userDetails.zip_code})</DetailValue>
                    </DetailRow>
                )}
            </SectionPaper>
            
            {/* One-time Costs Section */}
            <SectionPaper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                    One-time Costs
                </Typography>
                
                <DetailRow>
                    <DetailLabel>Activation Fee:</DetailLabel>
                    <DetailValue>${activationFee.toFixed(2)}</DetailValue>
                </DetailRow>
                
                {!userDetails.hasEsim && (
                    <DetailRow>
                        <DetailLabel>Shipping:</DetailLabel>
                        <DetailValue>
                            ${shippingCost.toFixed(2)}
                        </DetailValue>
                    </DetailRow>
                )}
                
                <DetailRow>
                    <DetailLabel>
                        <Info size={16} />
                        CircledIn Subscription:
                    </DetailLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <DetailValue>$0.00</DetailValue>
                        <Chip 
                            label="Using existing subscription" 
                            size="small" 
                            sx={{ 
                                ml: 1, 
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                bgcolor: '#d1fae5',
                                color: '#065f46',
                                border: 'none'
                            }} 
                        />
                    </Box>
                </DetailRow>
                
                <TotalRow>
                    <TotalLabel>Total Due Today:</TotalLabel>
                    <TotalValue>
                        ${totalDueToday.toFixed(2)}
                    </TotalValue>
                </TotalRow>
            </SectionPaper>
            
            {/* Transfer Guarantee Section */}
            <GuaranteeBox>
                <Box>
                    <ShieldCheck size={24} color="#059669" />
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ color: '#065f46', fontWeight: 600, mb: 0.5 }}>
                        CircledIn Transfer Guarantee
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#065f46' }}>
                        We'll handle the carrier switch process for you. If there are any issues, our support team will assist you every step of the way.
                    </Typography>
                </Box>
            </GuaranteeBox>
            
            <Box sx={{ 
                p: 2, 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                backgroundColor: '#f9fafb'
            }}>
                <Info size={18} color="#6b7280" />
                <Typography variant="body2" color="text.secondary">
                    By proceeding to payment, you agree to CircledIn's Terms of Service and authorize us to process this carrier transfer on your behalf.
                </Typography>
            </Box>
        </Box>
    );
};

export default TransferReviewAndConfirm;